import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, distinctUntilChanged, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of, Subject, Subscription } from 'rxjs';

import {
  CoreModalService,
  DeviceService,
  IMedia,
  PositionEnum, SdTabComponent,
  SdTabGroupComponent, SdTitleComponent,
  TabGroupLayoutEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { SdWysiwygComponent, SdWysiwygParagraphComponent } from '@seb/web-design-system/wysiwyg';
import {
  ProductCardGridComponent
} from '@seb/web-common/core/components/static/product-card-grid/product-card-grid.component';
import {
  RecipeSearchComponent,
  SearchRecipes
} from '@seb/web-common/core/components/recipe/recipe-search/recipe-search.component';
import { FaqSearchListComponent } from '../../components/faq-search-list/faq-search-list.component';
import {
  DcpListResponse,
  GlobalSearchResult,
  RecipeSearchItem,
  WC_CONFIG_PROVIDER,
  WcConfig
} from '@seb/web-common/core/models';
import {
  DatalayerService,
  GlobalSearchService,
  HobStateService,
  LazyLoadingService,
  RecipeService
} from '@seb/web-common/core/services';
import { areAllQueryParamMapEqual, areQueryParamMapEqual } from '@seb/web-common/core/utils';

@Component({
  selector: 'wc-search-result-page',
  templateUrl: './search-result-page.component.html',
  styleUrls: ['./search-result-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdTabGroupComponent,
    SdTabComponent,
    ProductCardGridComponent,
    RecipeSearchComponent,
    FaqSearchListComponent,
    SdWysiwygComponent,
    SdWysiwygParagraphComponent,
    TranslatePipe
  ],
  providers: [CoreModalService]
})
export class SearchResultPageComponent implements OnInit, OnDestroy {
  readonly textConfig = PositionEnum.center;
  readonly mediaConfig = PositionEnum.left;

  title: string;
  subtitle: string;
  content: string;
  image: IMedia;
  hasData = true;
  totals = { products: 0, accessories: 0, faq: 0, ifu: 0, recipes: 0 };
  currentTab: string;
  selectedTabIndex = 0;
  totalSearchQuery: number;
  savedRecipeSearchWithParams: SearchRecipes;
  isMobile$: boolean;
  mobileSubscription: Subscription;

  searchQuery: string;

  themesEnum = ThemesEnum;
  tabGroupLayoutEnum = TabGroupLayoutEnum;

  isRecipesEnabled = true;

  gridLoaded = false;
  useLazyLoad = false;

  readonly context = 'Search Results';

  @Input() class = '';

  private destroy$: Subject<void> = new Subject();
  private searchTabsByIndex: string[];

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-template'].filter(Boolean).join(' ');
  }

  constructor(
    private translateService: TranslateService,
    private globalSearchService: GlobalSearchService,
    private recipeService: RecipeService,
    private route: ActivatedRoute,
    private router: Router,
    private datalayerService: DatalayerService,
    private cdRef: ChangeDetectorRef,
    private lazyLoadingService: LazyLoadingService,
    private deviceService: DeviceService,
    private readonly hobStateService: HobStateService,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig
  ) {
    this.hobStateService.isRecipeEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isRecipesEnabled) => {
        this.isRecipesEnabled = isRecipesEnabled;
        this.useLazyLoad = this.lazyLoadingService.isEnabled;
        this.initTabsIndexes();
      });
  }

  ngOnInit(): void {
    this.mobileSubscription = this.deviceService.isDevice.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile$ = isMobile;
      this.cdRef.markForCheck();
    });

    this.route.queryParamMap
      .pipe(
        distinctUntilChanged((previous, current) => {
          return (
            areAllQueryParamMapEqual(previous, current, ['query', 'sortCode', 'target']) ||
            (previous.get('sortCode') !== current.get('sortCode') &&
              areQueryParamMapEqual(previous, current, 'query')) ||
            current.keys?.length === 0
          );
        }),
        withLatestFrom(this.hobStateService.isRecipeEnabled$),
        switchMap(([params, isRecipeEnabled]) => {
          this.gridLoaded = false;
          this.currentTab = params.has('target') ? params.get('target') : 'product';
          this.selectedTabIndex = this.targetToTabIndex(this.currentTab);
          this.searchQuery = params.get('query')?.split(':')[0].trim();
          const paramsWithAllTargets = {
            query: params.get('query'),
            isAccessorySearch: params.has('isAccessorySearch') ? params.get('isAccessorySearch') : false,
            target: 'product,accessoryProduct,faq,ifu',
            pageSize: 1
          };
          const dcpSearchResponse$ = isRecipeEnabled
            ? this.recipeService.search(this.searchQuery, 0, 0).pipe(catchError(() => of(undefined)))
            : of(undefined);
          return combineLatest([
            this.globalSearchService.globalSearch(paramsWithAllTargets).pipe(catchError(() => of(undefined))),
            dcpSearchResponse$
          ]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([suggestions, dcpSearchResponse]: [GlobalSearchResult, DcpListResponse<RecipeSearchItem>]) => {
        this.totals.products = suggestions?.productSearchPageData?.pagination?.totalResults || 0;
        this.totals.accessories = suggestions?.accessorySearchPageData?.pagination?.totalResults || 0;
        this.totals.faq = suggestions?.faqSearchPageData?.pagination?.totalResults || 0;
        this.totals.ifu = suggestions?.ifuSearchPageData?.pagination?.totalResults || 0;
        if (dcpSearchResponse) {
          this.totals.recipes = dcpSearchResponse?.page.totalElements || 0;
        }
        this.hasData =
          this.totals.products > 0 ||
          this.totals.accessories > 0 ||
          this.totals.faq > 0 ||
          this.totals.ifu > 0 ||
          (this.isRecipesEnabled && this.totals.recipes > 0);
        this.searchTracking(this.currentTab, this.totals);
        if (
          (this.currentTab === 'product' && this.totals.products === 0) ||
          (this.currentTab === 'accessoryProduct' && this.totals.accessories === 0) ||
          (this.currentTab === 'ifu' && this.totals.ifu === 0)
        ) {
          this.datalayerService.setProductList([], 0);
        }
        this.datalayerService.setSearchData(this.searchQuery, this.currentTab, this.totalSearchQuery);
        this.cdRef.markForCheck();
      });
    this.initNoResultsPage();
  }

  searchTracking(currentTab: string, totals: any): void {
    switch (currentTab) {
      case 'product':
        this.totalSearchQuery = totals?.products;
        break;
      case 'accessoryProduct':
        this.totalSearchQuery = totals?.accessories;
        break;
      case 'recipes':
        this.totalSearchQuery = totals?.recipes;
        break;
      case 'faq':
        this.totalSearchQuery = totals?.faq;
        break;
      case 'ifu':
        this.totalSearchQuery = totals?.ifu;
        break;
      default:
        this.totalSearchQuery = 0;
    }
  }

  changeTab(index: number): void {
    const newTab = this.searchTabsByIndex[index];
    if (newTab !== this.currentTab) {
      this.gridLoaded = false;
      this.currentTab = newTab;
      const searchQuery = this.searchQuery.split(':')[0].trim();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          query: searchQuery,
          target: this.currentTab,
          page: null
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  targetToTabIndex(target: string): number {
    return this.searchTabsByIndex.findIndex((tab) => target === tab);
  }

  initNoResultsPage(): void {
    this.title = this.translateService.instant('searchResultPage.noResults.title');
    this.subtitle = this.translateService.instant('searchResultPage.noResults.subTitle');
    this.content = `
       <ul class='is-list-checkmark'>
        <li>${this.translateService.instant('searchResultPage.noResults.firstAdvice')}</li>
        <li>${this.translateService.instant('searchResultPage.noResults.secondAdvice')}</li>
        <li>${this.translateService.instant('searchResultPage.noResults.thirdAdvice')}</li>
       </ul>
    `;
    this.image = { url: '/assets/images/no_search_result.jpg' };
  }

  alreadySearchedRecipeChange(event: SearchRecipes): void {
    this.savedRecipeSearchWithParams = event;
  }

  onGridLoaded(): void {
    this.gridLoaded = true;
    this.cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileSubscription?.unsubscribe();
  }

  private initTabsIndexes() {
    this.searchTabsByIndex = [
      'product',
      'accessoryProduct',
      ...(this.isRecipesEnabled ? ['recipes'] : []),
      'faq',
      'ifu'
    ];
  }

  getTotals() {
    return Object.values(this.totals).reduce((total, value) => total + value, 0);
  }
}
